import { Share, ShareMessage } from '../types/Share';

// CloudKit configuration
const CLOUDKIT_CONFIG = {
  containerIdentifier: 'iCloud.org.zimmelman.rz-sharing',
  environment: 'development' as const,
};

// CloudKit types
interface CloudKitRecord {
  recordName: string;
  recordType: string;
  fields: Record<string, { value: unknown }>;
  created?: { timestamp: number };
  modified?: { timestamp: number };
}

interface CloudKitResponse {
  records: CloudKitRecord[];
}

class CloudKitService {
  private container: unknown;
  private database: unknown;
  private isConfigured = false;
  private isAuthenticated = false;
  private userName: string = '';
  private userAvatarUrl: string = '';
  private apiToken: string = '';

  async configure(apiToken: string): Promise<void> {
    this.apiToken = apiToken;
    return new Promise((resolve, reject) => {
      // Load CloudKit JS from CDN
      if (!(window as unknown as Record<string, unknown>).CloudKit) {
        const script = document.createElement('script');
        script.src = 'https://cdn.apple-cloudkit.com/ck/2/cloudkit.js';
        script.async = true;
        script.onload = () => {
          try {
            this.initializeCloudKit(apiToken);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        script.onerror = () => reject(new Error('Failed to load CloudKit JS'));
        document.head.appendChild(script);
      } else {
        try {
          this.initializeCloudKit(apiToken);
          resolve();
        } catch (error) {
          reject(error);
        }
      }
    });
  }

  private initializeCloudKit(apiToken: string): void {
    const CloudKit = (window as unknown as Record<string, unknown>).CloudKit as {
      configure: (config: unknown) => { getDefaultContainer: () => unknown };
    };

    const config = CloudKit.configure({
      containers: [{
        containerIdentifier: CLOUDKIT_CONFIG.containerIdentifier,
        apiTokenAuth: {
          apiToken: apiToken,
          persist: true,
          signInButton: {
            id: 'apple-sign-in-button',
            theme: 'black',
          },
          signOutButton: {
            id: 'apple-sign-out-button',
            theme: 'black',
          },
        },
        environment: CLOUDKIT_CONFIG.environment,
      }],
    });

    this.container = config.getDefaultContainer();
    this.database = (this.container as { publicCloudDatabase: unknown }).publicCloudDatabase;
    this.isConfigured = true;
  }

  async setupAuth(): Promise<boolean> {
    this.ensureConfigured();

    const container = this.container as {
      setUpAuth: () => Promise<unknown>;
      whenUserSignsIn: () => Promise<unknown>;
      whenUserSignsOut: () => Promise<unknown>;
    };

    // Set up event listeners for sign-in/sign-out
    container.whenUserSignsIn().then((userIdentity: unknown) => {
      this.isAuthenticated = true;
      this.extractUserName(userIdentity);
      console.log('User signed in:', userIdentity);
      // Force page update to reflect auth state
      window.dispatchEvent(new CustomEvent('cloudkit-auth-change', { detail: { authenticated: true, userName: this.userName, userAvatarUrl: this.userAvatarUrl } }));
    });

    container.whenUserSignsOut().then(() => {
      this.isAuthenticated = false;
      this.userName = '';
      this.userAvatarUrl = '';
      console.log('User signed out');
      window.dispatchEvent(new CustomEvent('cloudkit-auth-change', { detail: { authenticated: false, userName: '', userAvatarUrl: '' } }));
    });

    // Check current auth status
    const userIdentity = await container.setUpAuth();
    this.isAuthenticated = !!userIdentity;
    if (userIdentity) {
      this.extractUserName(userIdentity);
    }

    return this.isAuthenticated;
  }

  private extractUserName(userIdentity: unknown): void {
    const identity = userIdentity as {
      nameComponents?: {
        givenName?: string;
        familyName?: string;
      };
      lookupInfo?: {
        emailAddress?: string;
      };
      // Possible avatar fields from CloudKit
      photoURL?: string;
      avatarURL?: string;
      userRecordName?: string;
    };

    if (identity?.nameComponents?.givenName) {
      const firstName = identity.nameComponents.givenName;
      const lastName = identity.nameComponents.familyName || '';
      this.userName = lastName ? `${firstName} ${lastName}` : firstName;
    } else if (identity?.lookupInfo?.emailAddress) {
      this.userName = identity.lookupInfo.emailAddress.split('@')[0];
    }

    // Extract avatar URL if available
    if (identity?.photoURL) {
      this.userAvatarUrl = identity.photoURL;
    } else if (identity?.avatarURL) {
      this.userAvatarUrl = identity.avatarURL;
    }
  }

  getUserName(): string {
    return this.userName;
  }

  getUserAvatarUrl(): string {
    return this.userAvatarUrl;
  }

  // Method to manually trigger sign-in if button doesn't work
  async signIn(): Promise<boolean> {
    this.ensureConfigured();

    const container = this.container as {
      fetchUserIdentity: () => Promise<unknown>;
    };

    try {
      const userIdentity = await container.fetchUserIdentity();
      this.isAuthenticated = !!userIdentity;
      return this.isAuthenticated;
    } catch (error) {
      console.error('Sign in failed:', error);
      return false;
    }
  }

  getAuthStatus(): boolean {
    return this.isAuthenticated;
  }

  private ensureConfigured(): void {
    if (!this.isConfigured) {
      throw new Error('CloudKit not configured. Call configure() first with your API token.');
    }
  }

  private extractCloudKitErrors(response: unknown): string {
    const resp = response as { _errors?: unknown[]; errors?: unknown[] };
    const errors = resp._errors || resp.errors || [];
    return JSON.stringify(errors, null, 2);
  }

  // Extract the CloudKit web auth token from the container cookie
  private getWebAuthToken(): string | null {
    const containerCookieName = CLOUDKIT_CONFIG.containerIdentifier;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      const equalsIndex = trimmed.indexOf('=');
      if (equalsIndex > 0) {
        const cookieName = trimmed.substring(0, equalsIndex);
        if (cookieName === containerCookieName) {
          return trimmed.substring(equalsIndex + 1);
        }
      }
    }
    console.warn('CloudKit auth cookie not found for container:', containerCookieName);
    return null;
  }

  // Upload an image as a CloudKit Asset via the REST API
  private async uploadImageAsset(base64Data: string): Promise<Record<string, unknown>> {
    const webAuthToken = this.getWebAuthToken();
    if (!webAuthToken) {
      // Log available cookies for debugging
      console.error('Available cookies:', document.cookie);
      throw new Error('No ckWebAuthToken found — cannot upload asset');
    }

    const containerID = CLOUDKIT_CONFIG.containerIdentifier;
    const environment = CLOUDKIT_CONFIG.environment;
    const authParams = `ckAPIToken=${encodeURIComponent(this.apiToken)}&ckWebAuthToken=${encodeURIComponent(webAuthToken)}`;

    // Step 1: Request an upload URL from CloudKit
    const tokenUrl = `https://api.apple-cloudkit.com/database/1/${containerID}/${environment}/public/assets/upload?${authParams}`;

    const tokenResponse = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tokens: [{ recordType: 'Share', fieldName: 'itemPhoto' }],
      }),
    });

    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      throw new Error(`Upload token request failed (${tokenResponse.status}): ${errorText}`);
    }

    const tokenData = await tokenResponse.json();
    const uploadInfo = tokenData.tokens?.[0];
    if (!uploadInfo?.url) {
      throw new Error(`No upload URL returned: ${JSON.stringify(tokenData)}`);
    }

    // Step 2: Upload the binary data to the returned URL
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const uploadResponse = await fetch(uploadInfo.url, {
      method: 'POST',
      headers: { 'Content-Type': 'image/jpeg' },
      body: bytes,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`Asset upload failed (${uploadResponse.status}): ${errorText}`);
    }

    const uploadResult = await uploadResponse.json();
    const singleFile = uploadResult.singleFile;
    if (!singleFile) {
      throw new Error(`No singleFile in upload response: ${JSON.stringify(uploadResult)}`);
    }

    // Return the asset reference for use in the record
    return {
      fileChecksum: singleFile.fileChecksum,
      size: singleFile.size,
      receipt: singleFile.receipt,
      wrappingKey: singleFile.wrappingKey,
      referenceChecksum: singleFile.referenceChecksum,
    };
  }

  async fetchShares(): Promise<Share[]> {
    this.ensureConfigured();

    const query = {
      recordType: 'Share',
      sortBy: [{ fieldName: 'itemName', ascending: true }],
    };

    const response = await (this.database as { performQuery: (query: unknown) => Promise<CloudKitResponse> })
      .performQuery(query);

    return response.records.map((record: CloudKitRecord) => this.mapRecordToShare(record));
  }

  async fetchShareMessages(): Promise<ShareMessage[]> {
    this.ensureConfigured();

    const query = {
      recordType: 'ShareMessage',
    };

    const response = await (this.database as { performQuery: (query: unknown) => Promise<CloudKitResponse> })
      .performQuery(query);

    return response.records.map((record: CloudKitRecord) => this.mapRecordToMessage(record));
  }

  async createShare(shareData: {
    itemName: string;
    itemDescription?: string;
    itemSize: string;
    itemQuantity: number;
    itemSharedBy: string;
    latitude: number;
    longitude: number;
    itemPhoto?: string;
  }): Promise<Share> {
    this.ensureConfigured();

    const fields: Record<string, { value: unknown }> = {
      itemName: { value: shareData.itemName },
      itemDescription: { value: shareData.itemDescription || '' },
      itemSize: { value: shareData.itemSize },
      itemQuantity: { value: shareData.itemQuantity },
      itemSharedBy: { value: shareData.itemSharedBy },
      itemLocation: {
        value: {
          latitude: shareData.latitude,
          longitude: shareData.longitude,
        },
      },
    };

    // Upload photo as a CloudKit Asset if provided
    if (shareData.itemPhoto) {
      try {
        const assetReference = await this.uploadImageAsset(shareData.itemPhoto);
        fields.itemPhoto = { value: assetReference };
      } catch (uploadError) {
        console.error('Photo asset upload failed:', uploadError);
        // Continue creating the share without the photo
      }
    }

    const record = {
      recordType: 'Share',
      fields,
    };

    // Use newRecordsBatch().create().commit() for CloudKit JS v2
    const database = this.database as {
      newRecordsBatch: () => {
        create: (record: unknown) => {
          commit: () => Promise<CloudKitResponse>;
        };
      };
      saveRecords: (records: unknown) => Promise<CloudKitResponse>;
    };

    try {
      // Try v2 method first
      const response = await database.newRecordsBatch().create(record).commit();

      if (response.records && response.records.length > 0) {
        return this.mapRecordToShare(response.records[0]);
      }
      console.error('CloudKit create errors:', this.extractCloudKitErrors(response));
    } catch (batchError) {
      console.error('CloudKit batch exception:', batchError);
      // Fallback to saveRecords
      const response = await database.saveRecords(record);

      if (response.records && response.records.length > 0) {
        return this.mapRecordToShare(response.records[0]);
      }
      console.error('CloudKit saveRecords errors:', this.extractCloudKitErrors(response));
    }

    throw new Error('Failed to create share');
  }

  async deleteShare(recordName: string): Promise<void> {
    this.ensureConfigured();

    const database = this.database as {
      newRecordsBatch: () => {
        delete: (record: { recordName: string; recordType: string }) => {
          commit: () => Promise<CloudKitResponse>;
        };
      };
      deleteRecords: (records: unknown) => Promise<CloudKitResponse>;
    };

    const record = {
      recordName,
      recordType: 'Share',
    };

    try {
      await database.newRecordsBatch().delete(record).commit();
    } catch (error) {
      await database.deleteRecords(record);
    }
  }

  async updateShareQuantity(recordName: string, itemQuantity: number): Promise<void> {
    if (itemQuantity < 0) {
      throw new Error('Item quantity cannot be negative');
    }

    await this.updateRecordFields('Share', recordName, {
      itemQuantity: { value: itemQuantity },
    });
  }

  async approveShareMessage(recordName: string): Promise<void> {
    await this.updateRecordFields('ShareMessage', recordName, {
      shareMessageApprovedBool: { value: true },
    });
  }

  private async updateRecordFields(
    recordType: 'Share' | 'ShareMessage',
    recordName: string,
    fields: Record<string, { value: unknown }>
  ): Promise<void> {
    this.ensureConfigured();

    const database = this.database as {
      newRecordsBatch: () => {
        update?: (record: unknown) => {
          commit: () => Promise<CloudKitResponse>;
        };
      };
      saveRecords: (records: unknown) => Promise<CloudKitResponse>;
    };

    const record = {
      recordName,
      recordType,
      fields,
    };

    try {
      const batch = database.newRecordsBatch();
      if (!batch.update) {
        throw new Error('CloudKit batch update API not available');
      }

      const response = await batch.update(record).commit();
      if (response.records && response.records.length > 0) {
        return;
      }
      console.error('CloudKit update errors:', this.extractCloudKitErrors(response));
    } catch (batchError) {
      console.error('CloudKit update exception:', batchError);

      const response = await database.saveRecords(record);
      if (response.records && response.records.length > 0) {
        return;
      }
      console.error('CloudKit saveRecords update errors:', this.extractCloudKitErrors(response));
    }

    throw new Error(`Failed to update ${recordType}`);
  }

  private extractAssetUrl(assetValue: unknown): string | undefined {
    if (!assetValue) return undefined;
    // CloudKit asset with downloadURL
    const asset = assetValue as { downloadURL?: string };
    if (asset.downloadURL) return asset.downloadURL;
    // Raw base64 string — detect format and add data URI prefix
    if (typeof assetValue === 'string') {
      if (assetValue.startsWith('data:') || assetValue.startsWith('http')) return assetValue;
      const mimeType = this.detectImageMimeType(assetValue);
      return `data:${mimeType};base64,${assetValue}`;
    }
    return undefined;
  }

  private detectImageMimeType(base64String: string): string {
    if (base64String.startsWith('/9j/')) return 'image/jpeg';
    if (base64String.startsWith('iVBOR')) return 'image/png';
    if (base64String.startsWith('R0lGOD')) return 'image/gif';
    if (base64String.startsWith('UklGR')) return 'image/webp';
    if (base64String.startsWith('Qk')) return 'image/bmp';
    if (base64String.startsWith('SUkq') || base64String.startsWith('TU0A')) return 'image/tiff';
    if (base64String.startsWith('AAAA')) return 'image/heic';
    return 'image/jpeg'; // default fallback
  }

  private mapRecordToShare(record: CloudKitRecord): Share {
    const fields = record.fields;
    const location = fields.itemLocation?.value as { latitude: number; longitude: number } | undefined;

    return {
      recordName: record.recordName,
      itemName: (fields.itemName?.value as string) || '',
      itemDescription: (fields.itemDescription?.value as string) || undefined,
      itemSize: (fields.itemSize?.value as string) || '',
      itemQuantity: (fields.itemQuantity?.value as number) || 0,
      itemSharedBy: (fields.itemSharedBy?.value as string) || '',
      itemLocation: {
        latitude: location?.latitude || 0,
        longitude: location?.longitude || 0,
      },
      itemImage: this.extractAssetUrl(fields.itemPhoto?.value) || this.extractAssetUrl(fields.itemImage?.value),
      createdTimestamp: record.created ? new Date(record.created.timestamp).toISOString() : '',
      modifiedTimestamp: record.modified ? new Date(record.modified.timestamp).toISOString() : '',
    };
  }

  private mapRecordToMessage(record: CloudKitRecord): ShareMessage {
    const fields = record.fields;
    const parentRef = fields.shareParent?.value as { recordName: string } | undefined;

    return {
      recordName: record.recordName,
      shareParent: parentRef?.recordName || '',
      shareMessageContents: (fields.shareMessageContents?.value as string) || '',
      shareMessageItemRequestedBy: (fields.shareMessageItemRequestedBy?.value as string) || '',
      shareMessageApprovedBool: (fields.shareMessageApprovedBool?.value as boolean) || false,
      createdTimestamp: record.created ? new Date(record.created.timestamp).toISOString() : '',
    };
  }
}

export const cloudKitService = new CloudKitService();
